import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Switch, Route, Redirect, useLocation, BaseLocationHook } from "wouter";
import { ScrollResetRoute } from "@/components/layout/scroll-reset-route";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SeamlessSubscriptionGate } from "@/components/subscription/seamless-subscription-gate";
import NotFound from "@/pages/not-found";
import Sidebar from "@/components/layout/sidebar";
import { SetupTour } from "@/components/onboarding/setup-tour";
import Header from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import AIChatWidget from "@/components/layout/ai-chat-widget";
import { ScrollRestoration } from "@/components/layout/scroll-restoration";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { ErrorBoundary } from "@/components/error-boundary";
import Dashboard from "@/pages/dashboard";
import Applications from "@/pages/applications";
import ApplicationDetail from "@/pages/application-detail";
import Licenses from "@/pages/licenses";
import LicenseDetail from "@/pages/license-detail";
import LicenseAssignEmployeesPage from "@/pages/license-assign-employees";
import Analytics from "@/pages/analytics";
import CostManagement from "@/pages/cost-management";
// Recommendation detail now handled by dialog component
// Consolidated into People page
import TeamMembers from "@/pages/team-members";
import People from "@/pages/people";
import TeamManagement from "@/pages/team-management";
import Settings from "@/pages/settings";
import ModernSettings from "@/pages/modern-settings";
import EmailApprovals from "@/pages/email-approvals";
import SettingsPage from "@/pages/settings-page";
import PaymentCardsPage from "@/pages/payment-cards-page";
import DataImportPage from "@/pages/data-import-simplified";
import VerifyTenant from "@/pages/verify-tenant";

// Admin pages 
import AdminDashboard from "@/pages/admin/index";
import TenantsPage from "@/pages/admin/tenants";
import BillingPage from "@/pages/admin/billing";
import FraudPage from "@/pages/admin/fraud";
import EmailPage from "@/pages/admin/email";
import EmailTemplatesPage from "@/pages/admin/email-templates";
import AutomationBuilderPage from "@/pages/admin/automation-builder";
import CampaignsPage from "@/pages/admin/Campaigns";
import PlansPage from "@/pages/admin/plans";
import AnalyticsPage from "@/pages/admin/analytics";
import ControlsPage from "@/pages/admin/controls-fixed";
import AdminSettingsPage from "@/pages/admin/admin-settings";
import SecurityPage from "@/pages/admin/security";
import PublicSecurityPage from "@/pages/security";
import InvoicesPage from "@/pages/admin/invoices";
import CouponsPage from "@/pages/admin/coupons";
import AdminActivityPage from "@/pages/admin/activity";
import AdminAccess from "@/pages/admin-access";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import ContactManagementPage from "@/pages/admin/contact-management";
import ImportHistoryPage from "@/pages/admin/import-history";
import BlogManagementPage from "@/pages/admin/blog-management";
import CaseStudyManagement from "@/pages/admin/case-study-management";

// Auth and public pages
import ResetPassword from "@/pages/reset-password";
import LoginPage from "@/pages/login";
import AuthPage from "@/pages/auth-page";
import RegisterPage from "@/pages/register";
import OnboardingPage from "@/pages/onboarding";
import LandingPage from "@/pages/landing-page";
import AboutPage from "@/pages/about";
import PrivacyPolicyPage from "@/pages/privacy-policy";
import CookiePolicyPage from "@/pages/cookie-policy";
import TermsAndConditionsPage from "@/pages/terms-and-conditions";
import RefundPolicyPage from "@/pages/refund-policy";
import ContactPage from "@/pages/contact";
import FeaturesPage from "@/pages/features";
import PricingPage from "@/pages/pricing";
import BlogPage from "@/pages/blog";
import BlogPostPage from "@/pages/blog-post";
import CaseStudiesPage from "@/pages/case-studies";
import CaseStudyPage from "@/pages/case-study";
import AcceptInvitation from "@/pages/accept-invitation";

import CheckoutPage from "@/pages/checkout-page";
import HelpCenter from "@/pages/help-center";
import HelpArticle from "@/pages/help-article";
import SchemaManagement from "@/pages/schema-management";
import Renewals from "@/pages/renewals";
import BillingHistory from "@/pages/billing-history";
import Activity from "@/pages/activity";
import SubscriptionPage from "@/pages/subscription";
import ModernSubscriptionPage from "@/pages/modern-subscription";
import NotificationsPage from "@/pages/notifications";
import CategoryManagement from "@/pages/settings/category-management";
import { ProtectedRoute } from "./lib/protected-route";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { AppSettingsProvider } from "@/hooks/use-app-settings";
import { TenantProvider } from "@/components/tenant/tenant-provider";
import { AppLoadingScreen } from "@/components/ui/app-loading-screen";
import { AppStateProvider } from "@/hooks/use-app-state";
import { CurrencyProvider } from "@/components/currency/currency-provider";
import { SidebarProvider, useSidebar } from "@/hooks/use-sidebar";


// Custom hook to handle base path issues in production deployment
const useHashLocation: BaseLocationHook = () => {
  const [location, setLocation] = useState(() => window.location.pathname);
  
  // Remove /client/ from paths in production
  const cleanPath = location.replace(/\/client\//g, '/');
  
  const handleLocationChange = () => {
    // Always use the clean path without /client/
    let currentPath = window.location.pathname;
    currentPath = currentPath.replace(/\/client\//g, '/');
    
    if (currentPath !== cleanPath) {
      setLocation(currentPath);
    }
  };
  
  useEffect(() => {
    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);
  
  const navigate = useCallback((to: string) => {
    // Clean any /client/ from the path
    const cleanTo = to.replace(/\/client\//g, '/');
    
    // Update history and location
    window.history.pushState(null, '', cleanTo);
    setLocation(cleanTo);
  }, []);
  
  return [cleanPath, navigate];
};

function AppShell({ children }: { children: React.ReactNode }) {
  const { collapsed } = useSidebar();

  // Hide any GTM-injected live chat widgets (Chatwoot, Crisp, Intercom etc.) in the app
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-external-chat';
    style.textContent = `
      chat-widget, chat-widget.hydrated,
      [data-loader-instance*="leadconnector"],
      [src*="leadconnectorhq.com"],
      .woot-widget-holder, .woot--bubble-holder,
      #crisp-chatbox, .crisp-client,
      #intercom-container, .intercom-launcher {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => { document.getElementById('hide-external-chat')?.remove(); };
  }, []);

  return (
    <div className="app-layout min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 animate-in">
      <SetupTour />
      <Sidebar />
      <div className="flex flex-col overflow-hidden min-h-screen">
        <Header />
        <main
          id="main-content"
          className={`main-content flex-1 p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-white/50 to-blue-50/20 backdrop-blur-sm scrollbar-hide transition-all duration-300 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}
        >
          {children}
        </main>
        <Footer />
      </div>
      <AIChatWidget />
    </div>
  );
}

/**
 * AppRoutes handles the main routing logic for the application.
 * It implements a Replit-style URL pattern where:
 * 1. Non-authenticated users see public pages (/, /login, /register)
 * 2. Authenticated users are directed to their dashboard
 * 3. Clear, consistent URL paths are used throughout
 */
function AppRoutes() {
  const { user, isLoading, isSuperAdmin } = useAuth();
  const [location] = useLocation();
  const [, navigate] = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [initStage, setInitStage] = useState('loading');
  
  // Strip any /client/ from the current path for consistency
  const actualPath = location.replace(/\/client\//g, '/');

  // Simplified path handling - only redirect once when stable
  useEffect(() => {
    if (isLoading) return; // Don't do anything while loading
    
    // Clean /client/ paths only if they exist
    if (location.includes('/client/')) {
      const cleanPath = location.replace('/client/', '/');
      if (cleanPath !== '/' && cleanPath !== '/client') {
        navigate(cleanPath);
        return;
      }
    }
  }, [isLoading, location, navigate]);

  // Route authenticated users to appropriate dashboards
  useEffect(() => {
    if (!user || isLoading) return;
    
    // Don't redirect if user is on payment success page
    if (location.includes('/payment-success')) {
      return;
    }
    
    // Check if user needs to complete onboarding first
    if (!user.onboardingCompleted && actualPath !== '/onboarding') {
      navigate('/onboarding');
      return;
    }
    
    // Super admin users go to admin dashboard
    if (user.isSuperAdmin === true || user.username === 'Administrator') {
      if (location === '/' || location === '/dashboard') {
        navigate('/admin');
        return;
      }
    }
    
    // Regular tenant users stay on tenant dashboard
    // No subscription blocking for tenant admins like "manoj"
    if (location === '/' && !user.isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
  }, [user, isLoading, location, navigate, actualPath]);

  // Streamlined initialization to eliminate flickering
  useEffect(() => {
    if (isLoading) {
      setInitStage('auth');
      setIsInitialized(false);
      return;
    }

    // Initialize immediately once auth is complete
    if (!isInitialized) {
      setInitStage('ready');
      setIsInitialized(true);
    }
  }, [isLoading, isInitialized]);

  // Check for pending payment to prevent showing subscription page after registration
  const isPendingPayment = sessionStorage.getItem('pendingPayment') === 'true';
  
  // CRITICAL FIX: Ensure authentication state is always a proper boolean to prevent null states
  const isAuthenticated = Boolean(user && !isLoading && isInitialized);
  
  // Manage HTML/body classes to control scrolling behavior
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById('root');
    
    if (isAuthenticated) {
      // Remove landing-mode classes for authenticated users (disable page-level scrolling)
      htmlElement.classList.remove('landing-mode');
      bodyElement.classList.remove('landing-mode');
      rootElement?.classList.remove('landing-mode');
    } else {
      // Add landing-mode classes for public pages (enable page-level scrolling)
      htmlElement.classList.add('landing-mode');
      bodyElement.classList.add('landing-mode');
      rootElement?.classList.add('landing-mode');
    }
  }, [isAuthenticated]);
  const isAuthenticating = isLoading || (!user && !isInitialized) || isPendingPayment;
  
  const publicPaths = [
    "/", 
    "/login", 
    "/register", 
    "/onboarding",
    "/auth",
    "/checkout",
    "/reset-password",
    "/about",
    "/pricing",
    "/features",
    "/contact",
    "/blog",
    "/case-studies",
    "/security",
    "/privacy-policy",
    "/cookie-policy",
    "/refund-policy",
    "/terms-and-conditions",
    "/verify-tenant",
    "/landingpage",
    "/admin-access",
    "/accept-invitation" // Special admin access path for testing
  ];
  
  // Show loading state only during actual authentication loading
  if (isAuthenticating) {
    const message = isPendingPayment ? "Processing payment..." : "Starting application...";
    return <AppLoadingScreen message={message} />;
  }

  // Debug routing
  console.log("App routing debug:", { actualPath, isAuthenticated, user: !!user, isLoading });

  // Handle root path routing with ZERO flicker
  if (actualPath === "/") {
    if (isAuthenticated) {
      // Super admin should go directly to admin dashboard
      if (isSuperAdmin) {
        return <Redirect to="/admin" />;
      }
      // Regular users go directly to tenant dashboard - no subscription gate needed here
      return <Redirect to="/dashboard" />;
    } else {
      return <LandingPage />;
    }
  }
  
  const isPublicPath = publicPaths.some(path => 
    actualPath === path || actualPath.startsWith(`${path}/`)
  );
  
  console.log("Public path check:", { actualPath, isPublicPath, publicPaths });
  
  // Only redirect to landing for protected routes, but allow auth pages to render normally
  if (!isAuthenticated && !isPublicPath && !actualPath.startsWith('/auth') && !actualPath.startsWith('/login') && !actualPath.startsWith('/register')) {
    console.log("Redirecting to landing - not authenticated and not public path");
    return <LandingPage />;
  }

  // Admin-only routes
  // Note: '/settings' and '/profile' are intentionally excluded — every authenticated
  // user needs the Profile tab there (e.g. to request an email change); the page itself
  // restricts which tabs a non-admin can see.
  const adminOnlyRoutes = [
    '/analytics',
    '/cost-management',
    '/renewals',
    '/activity',
    '/payment-cards',
    '/people',
    '/team-members',
    '/data-import'
  ];
  
  // Super admin-only routes
  const superAdminOnlyRoutes = [
    '/admin',
    '/admin/tenants',
    '/admin/billing',
    '/admin/fraud',
    '/admin/email',
    '/admin/plans',
    '/admin/controls',
    '/admin/analytics',
    '/admin/invoices',
    '/admin/coupons',
    '/admin/security',
    '/admin/settings',
    '/admin/activity',
    '/admin/blog',
    '/admin/case-studies',
    '/admin/contact'
  ];
  
  // Check if user is trying to access an admin-only route without being an admin
  const isAdminRoute = adminOnlyRoutes.some(route => 
    actualPath === route || actualPath.startsWith(`${route}/`)
  );
  
  // Check if user is trying to access a super admin-only route without being a super admin
  const isSuperAdminRoute = superAdminOnlyRoutes.some(route =>
    actualPath === route || actualPath.startsWith(`${route}/`)
  );
  
  // We already have the isSuperAdmin from useAuth(), so no need to recreate it
  
  if (isAuthenticated && user?.role !== 'admin' && isAdminRoute) {
    return <Redirect to="/licenses" />;
  }
  
  if (isAuthenticated && !isSuperAdmin && isSuperAdminRoute) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <Switch>
      {/* Accept invitation page - public route */}
      <Route path="/accept-invitation">
        <AcceptInvitation />
      </Route>
      
      {/* Alternative landing page path - must be before root path to work properly */}
      <Route path="/landingpage">
        <LandingPage />
      </Route>
      
      {/* Main landing page */}
      <Route path="/">
        {!user ? <LandingPage /> : (isSuperAdmin ? <Redirect to="/admin" /> : <Redirect to="/dashboard" />)}
      </Route>
      
      {/* Super Admin login and access - using standalone admin layout with no tenant sidebar */}
      <Route path="/admin">
        {user && isSuperAdmin ? <AdminDashboard /> : <LoginPage adminMode={true} />}
      </Route>
      
      {/* Super Admin subpages - direct component rendering without tenant layout */}
      <Route path="/admin/tenants">
        {user && isSuperAdmin ? <TenantsPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/billing">
        {user && isSuperAdmin ? <BillingPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/fraud">
        {user && isSuperAdmin ? <FraudPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/email">
        {user && isSuperAdmin ? <EmailPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/email/templates">
        {user && isSuperAdmin ? <EmailTemplatesPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/email/workflows/:id/automation">
        {user && isSuperAdmin ? <AutomationBuilderPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/campaigns">
        {user && isSuperAdmin ? <CampaignsPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      {/* Additional Admin Routes */}
      <Route path="/admin/plans">
        {user && isSuperAdmin ? <PlansPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/controls">
        {user && isSuperAdmin ? <ControlsPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/contact">
        {user && isSuperAdmin ? <ContactManagementPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/import-history">
        {user && isSuperAdmin ? <ImportHistoryPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/schema">
        {user && isSuperAdmin ? <SchemaManagement /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/analytics">
        {user && isSuperAdmin ? <AnalyticsPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/invoices">
        {user && isSuperAdmin ? <InvoicesPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/coupons">
        {user && isSuperAdmin ? <CouponsPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/security">
        {user && isSuperAdmin ? <SecurityPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/activity">
        {user && isSuperAdmin ? <AdminActivityPage /> : <LoginPage adminMode={true} />}
      </Route>
      
      <Route path="/admin/settings">
        {user && isSuperAdmin ? <AdminSettingsPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/blog">
        {user && isSuperAdmin ? <BlogManagementPage /> : <LoginPage adminMode={true} />}
      </Route>

      <Route path="/admin/case-studies">
        {user && isSuperAdmin ? <CaseStudyManagement /> : <LoginPage adminMode={true} />}
      </Route>

      {/* Tenant login and access */}
      <Route path="/tenant">
        {user ? <Redirect to="/dashboard" /> : <LoginPage tenantMode={true} />}
      </Route>
      
      {/* Reset password route - must be before auth route */}
      <Route path="/reset-password">
        <ResetPassword />
      </Route>
      
      {/* Auth page with login and forgot password */}
      <Route path="/auth">
        <AuthPage />
      </Route>
      
      <Route path="/login">
        <LoginPage />
      </Route>
      
      <Route path="/register">
        <RegisterPage />
      </Route>
      
      <Route path="/onboarding">
        <OnboardingPage />
      </Route>
      
      <Route path="/about">
        <AboutPage />
      </Route>

      <Route path="/privacy-policy">
        <PrivacyPolicyPage />
      </Route>

      <Route path="/cookie-policy">
        <CookiePolicyPage />
      </Route>

      <Route path="/terms-and-conditions">
        <TermsAndConditionsPage />
      </Route>

      <Route path="/refund-policy">
        <RefundPolicyPage />
      </Route>

      <Route path="/contact">
        <ContactPage />
      </Route>
      
      <Route path="/blog">
        <BlogPage />
      </Route>
      
      <Route path="/blog/:slug">
        <BlogPostPage />
      </Route>
      
      <Route path="/case-studies">
        <CaseStudiesPage />
      </Route>
      
      <Route path="/case-studies/:slug">
        <CaseStudyPage />
      </Route>
      
      <Route path="/features">
        <FeaturesPage />
      </Route>
      
      <Route path="/pricing">
        <PricingPage />
      </Route>

      <Route path="/security">
        <PublicSecurityPage />
      </Route>

      <Route path="/verify-tenant">
        <VerifyTenant />
      </Route>
      
      <Route path="/checkout">
        <CheckoutPage />
      </Route>
      
      <Route path="/thank-you">
        <ScrollResetRoute path="/thank-you" component={lazy(() => import('@/pages/thank-you-page'))} />
      </Route>
      
      <Route path="/payment-success">
        <ScrollResetRoute path="/payment-success" component={lazy(() => import('@/pages/payment-success'))} />
      </Route>
      
      {isAuthenticated && !isSuperAdmin ? (
        // Protected routes with app layout - only show when user is authenticated AND not loading AND not super admin
        <Route>
          <SeamlessSubscriptionGate>
            <SidebarProvider>
              <AppShell>
                <div className="container-responsive">
                  <Switch>
                    {/* User-accessible routes */}
                    <ScrollResetRoute path="/applications" component={Applications} />
                    <ScrollResetRoute path="/applications/:id" component={ApplicationDetail} />
                    <ScrollResetRoute path="/licenses" component={Licenses} />
                    <ScrollResetRoute path="/licenses/:id" component={LicenseDetail} />
                    <ScrollResetRoute path="/licenses/:id/assign-employees" component={LicenseAssignEmployeesPage} />
                    <ScrollResetRoute path="/data-import" component={DataImportPage} />
                    <ScrollResetRoute path="/help" component={HelpCenter} />
                    <ScrollResetRoute path="/docs/article/:id" component={HelpArticle} />
                    <ScrollResetRoute path="/docs/:category" component={HelpArticle} />

                    {/* Common routes accessible to all authenticated users */}
                    <ScrollResetRoute path="/dashboard" component={Dashboard} />
                    <ScrollResetRoute path="/subscription" component={ModernSubscriptionPage} />
                    <ScrollResetRoute path="/notifications" component={NotificationsPage} />
                    {/* Settings is open to everyone (e.g. to request an email change from
                        Profile) — ModernSettings itself restricts non-admins to the Profile tab. */}
                    <ScrollResetRoute path="/settings">
                      <ModernSettings />
                    </ScrollResetRoute>

                    {/* Regular Admin-only routes */}
                    {user?.role === 'admin' && (
                      <>
                        <ScrollResetRoute path="/analytics" component={Analytics} />
                        <ScrollResetRoute path="/cost-management" component={CostManagement} />
                        <ScrollResetRoute path="/renewals" component={Renewals} />
                        <ScrollResetRoute path="/billing-history" component={BillingHistory} />
                        <ScrollResetRoute path="/activity" component={Activity} />
                        <ScrollResetRoute path="/payment-cards" component={PaymentCardsPage} />
                        <ScrollResetRoute path="/people" component={People} />
                        <ScrollResetRoute path="/team-members/:id" component={TeamMembers} />
                        <ScrollResetRoute path="/team" component={TeamManagement} />
                        <ScrollResetRoute path="/settings/categories" component={CategoryManagement} />
                        <ScrollResetRoute path="/settings/currency">
                          <Redirect to="/settings" />
                        </ScrollResetRoute>
                        <ScrollResetRoute path="/email-approvals" component={EmailApprovals} />
                      </>
                    )}

                    <Route component={NotFound} />
                  </Switch>
                </div>
              </AppShell>
            </SidebarProvider>
          </SeamlessSubscriptionGate>
        </Route>
      ) : (
        // Public fallback routes and loading states
        <Route>
          {isAuthenticating ? (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
              <div className="text-center">
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">Loading your workspace</h2>
                <p className="text-gray-500">Please wait while we set up your dashboard...</p>
              </div>
            </div>
          ) : (
            // Don't redirect to login, show landing page instead
            <LandingPage />
          )}
        </Route>
      )}
    </Switch>
  );
}

// Hide LeadConnector chat widget on auth/onboarding pages
function HideExternalChatOnAuthPages() {
  const [location] = useLocation();
  const AUTH_PATHS = ['/login', '/register', '/onboarding', '/auth', '/reset-password', '/tenant', '/verify-tenant', '/checkout'];
  const shouldHide = AUTH_PATHS.some(p => location.startsWith(p));

  useEffect(() => {
    const id = 'hide-chat-auth-pages';
    if (shouldHide) {
      if (!document.getElementById(id)) {
        const style = document.createElement('style');
        style.id = id;
        style.textContent = `chat-widget, chat-widget.hydrated { display: none !important; }`;
        document.head.appendChild(style);
      }
    } else {
      document.getElementById(id)?.remove();
    }
  }, [shouldHide]);

  return null;
}

function PublicTrialButton() {
  const [location] = useLocation();
  const websitePages = ["/", "/about", "/pricing", "/features", "/contact", "/blog", "/case-studies", "/security", "/privacy-policy", "/cookie-policy", "/refund-policy", "/terms-and-conditions", "/landingpage"];
  const isWebsitePage = websitePages.some(p => location === p || location.startsWith(`${p}/`));
  if (!isWebsitePage) return null;
  return (
    <a
      href="/register"
      className="fixed z-40 bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-xl transition-all duration-200 flex items-center justify-center"
      style={{
        top: '72%',
        right: '0',
        transform: 'translateY(-50%)',
        writingMode: 'vertical-rl',
        textOrientation: 'mixed',
        letterSpacing: '0.12em',
        fontSize: '13px',
        padding: '12px 5px',
        borderRadius: '10px 0 0 10px',
      }}
    >
      FREE TRIAL
    </a>
  );
}

function App() {
  useEffect(() => {
    // Remove static server-injected meta tags so React Helmet Async takes sole ownership.
    // Without this, the initial page's injected tags persist in the DOM across client-side
    // navigations and the SEO tool reads the orphaned static tag instead of Helmet's.
    const selectors = [
      'link[rel="canonical"]:not([data-rh])',
      'meta[name="description"]:not([data-rh])',
      'meta[property="og:url"]:not([data-rh])',
      'meta[property="og:title"]:not([data-rh])',
      'meta[property="og:description"]:not([data-rh])',
      'meta[name="twitter:title"]:not([data-rh])',
      'meta[name="twitter:description"]:not([data-rh])',
    ];
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => el.remove());
    });
  }, []);

  return (
    <div className="min-h-screen">
      <ErrorBoundary fallbackTitle="SaaSGama could not load this page">
        <QueryClientProvider client={queryClient}>
          <CurrencyProvider>
            <AuthProvider>
              <AppStateProvider>
                <TenantProvider>
                  <AppSettingsProvider>
                    <TooltipProvider>
                      <Toaster />
                      <ScrollRestoration />
                      <HideExternalChatOnAuthPages />
                      <AppRoutes />
                      <PublicTrialButton />
                      <CookieConsentBanner />
                    </TooltipProvider>
                  </AppSettingsProvider>
                </TenantProvider>
              </AppStateProvider>
            </AuthProvider>
          </CurrencyProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
